import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router'
import { useEntitlement } from '../../platform/access/useEntitlement'
import { localToday, type LocalDate } from '../../platform/localDate'
import { useCategories } from '../categories/useCategories'
import { emitBreakdownViewChanged, emitMerchantExpanded, emitViewed } from './cashflowAnalytics'
import type { CategoryMap } from './cashflowCategories'
import { clampPeriod, lowerBound, readView, writeView, type CashflowView } from './cashflowPeriod'
import { detailPath, type LinkTarget } from './cashflowScope'
import { CashflowScreen } from './CashflowScreen'
import { useCashflowHistory, useCashflowPeriod } from './useCashflow'
import { useViewChange } from './useViewChange'

/** What the last answered period call said about the account's bounds. */
type Bounds = { earliest: LocalDate | null; today: LocalDate }

/**
 * The `/cashflow` route. The screen is presentational; this owns what it must
 * not: the URL (the selected period lives in the query string), the two
 * requests, the entitlement and the analytics.
 *
 * The period call goes first: its `available.earliest_transaction_date` decides
 * where the history call starts, and whether there is any history at all.
 * Those bounds and the server's `window.today` are kept from the last call that
 * answered, so a step that fails keeps the chart and the floor (R9): the
 * account did not lose its history because one period would not load.
 */
export function Cashflow() {
  const [params, setParams] = useSearchParams()
  // One day for the whole mount: a render across midnight must not re-key the queries.
  const today = useMemo(() => localToday(), [])
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, [])
  const entitled = useEntitlement('cashflow_details')

  // `null` until a period call has answered.
  const [bounds, setBounds] = useState<Bounds | null>(null)
  // The server's day once it has said, the client's before: the URL, the clamp,
  // the header and the history window all read this one day.
  const asOf = bounds?.today ?? today
  // `undefined` until the period call has answered; `null` when there are no transactions.
  const earliest = bounds === null ? undefined : bounds.earliest

  const asked = readView(params, asOf)
  const granularity = asked.granularity
  // Once the bounds are known, a period outside them is pulled in (R9, R7) —
  // before it is fetched, so an out-of-range URL never asks for its own period.
  const lower = lowerBound(earliest ?? null, granularity, asOf)
  const clamped = clampPeriod(asked.period, granularity, lower, asOf)
  const view = useMemo<CashflowView>(() => ({ granularity, period: clamped }), [granularity, clamped])

  const period = useCashflowPeriod({ view, today, tz, details: entitled })
  const answered = period.state.status === 'success' ? period.state.data.window : null
  if (answered !== null && (bounds === null || bounds.earliest !== answered.earliest || bounds.today !== answered.today)) {
    // Adjusting state during render (not an effect): the next render reads the new bounds.
    setBounds({ earliest: answered.earliest, today: answered.today })
  }

  // Compared with the RAW query value, so a hand-typed mid-period date (2026-03-15)
  // or an out-of-range one is rewritten to the canonical first day.
  const raw = params.get('period')
  useEffect(() => {
    const canonical = writeView({ granularity, period: clamped }, asOf)
    // REPLACE, not push: the non-canonical URL was never a place to go Back to.
    if (raw !== canonical.get('period')) setParams(canonical, { replace: true })
  }, [raw, clamped, granularity, setParams, asOf])

  const history = useCashflowHistory({ granularity, today: asOf, tz, enabled: entitled, earliest })
  // A period call that failed before it ever answered has not said where history
  // starts, so there is no chart region to wait in: its Try again is the way on.
  const periodNeverAnswered = bounds === null && period.state.status === 'error'

  const categoriesState = useCategories()
  const categoryRows = categoriesState.status === 'success' ? categoriesState.data : null
  // While categories load or fail the screen still renders; an unknown id reads "Other category".
  const categories = useMemo<CategoryMap>(
    () =>
      new Map((categoryRows ?? []).map((c) => [c.id, { name: c.name, parentId: c.parent_id ?? null, icon: c.icon ?? null }])),
    [categoryRows],
  )

  const viewed = useRef(false)
  useEffect(() => {
    if (viewed.current) return
    viewed.current = true
    // The ref makes it once per mount, with the view and entitlement the screen opened on.
    emitViewed(view, !entitled)
  }, [view, entitled])

  // A push, so Back restores the period before; a keyboard run through the chart replaces after its first key.
  const write = useCallback((next: CashflowView, replace: boolean) => setParams(writeView(next, asOf), { replace }), [setParams, asOf])
  const locationKey = useLocation().key
  const onViewChange = useViewChange(view.granularity, write, locationKey)

  const rowLink = useCallback((t: LinkTarget) => detailPath(t, view, asOf), [view, asOf])

  return (
    <CashflowScreen
      view={view}
      today={asOf}
      earliest={earliest}
      entitled={entitled}
      // No chart region for a free user (the locked picture stands instead),
      // for an account with no transactions at all, or before the period answers.
      history={entitled && earliest !== null && !periodNeverAnswered ? history : null}
      period={period}
      categories={categories}
      onViewChange={onViewChange}
      onMerchantsExpanded={emitMerchantExpanded}
      onBreakdownViewChange={emitBreakdownViewChanged}
      rowLink={rowLink}
    />
  )
}
